import React, { useState, useMemo } from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { Card, CardRibbon, Flex, Text, Skeleton } from 'vvs-uikit'
import Tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import ExpandableSectionButton from 'components/ExpandableSectionButton'
import { BASE_ADD_LIQUIDITY_URL, BASE_ZAP_URL } from 'config'
import { ecoFarm } from 'config/constants/ecoBoost'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useFarmBoostFactor } from 'state/farmBoost/useFarmBoostFactor'
import { getExplorerLink, isBlindMode } from 'utils'
import { getAddress } from 'utils/addressHelpers'
import getLiquidityUrlPathParts from 'utils/getLiquidityUrlPathParts'
import getZapUrlPathParts from 'utils/getZapUrlPathParts'
import getFiatSymbol from 'utils/getFiatSymbol'
import getFiatValue from 'utils/getFiatValue'
import { getVVSRewardPerDay } from 'utils/getFarmRewards'
import { FarmWithStakedValue } from 'views/Farms/interfaces'
import { isEcoFarmSupported } from 'utils/isNftStakingSupported'
import { BoostedTag, BoostAvailableTag, BoostExpiredTag } from 'components/Tags'
import { MAX_TOTAL_BOOST_FACTOR } from 'config/constants/farmBoost'
import { useGetEcoBoostFactor } from 'views/Farms/hooks/useGetEcoBoostFactor'
import isBeforeNow from 'utils/isBeforeNow'
import isBoostFarm from 'utils/isBoostFarm'
import BoostAction from './BoostAction'
import DetailsSection from './DetailsSection'
import CardHeading from './CardHeading'
import CardActionsContainer from './CardActionsContainer'
import ApyButton from './ApyButton'
import BoostPanel from './BoostPanel'
import EcoBoostPanel from './EcoBoostPanel'
import PoolRewards from './PoolRewards'
import Countdown from '../Countdown'

const StyledCard = styled(Card)<{ isFinished?: boolean }>`
  align-self: start;
  color: ${({ isFinished, theme }) => theme.colors[isFinished ? 'textDisabled' : 'text']};
`

const FarmCardInnerContainer = styled(Flex)`
  flex-direction: column;
  justify-content: space-around;
  padding: 24px;
`

const ExpandingWrapper = styled.div<{ expanded }>`
  padding-top: 24px;
  padding-bottom: ${({ expanded }) => (expanded ? `0` : `24px`)};
  border-top: 2px solid ${({ theme }) => theme.colors.cardBorder};
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.backgroundAlt5};
`

interface FarmCardProps {
  farm: FarmWithStakedValue
  displayApr: string
  removed: boolean
  vvsPrice?: BigNumber
  account?: string
  farms?: any[]
  onDashboard?: boolean
  xVVSStaked?: boolean
}

const FarmCard: React.FC<FarmCardProps> = ({
  farm,
  displayApr,
  removed,
  vvsPrice,
  account,
  farms,
  onDashboard,
  xVVSStaked,
}) => {
  const { t } = useTranslation()
  const { chainId } = useActiveWeb3React()

  const [showExpandableSection, setShowExpandableSection] = useState(false)
  const hasFarmStarted = useMemo(() => isBeforeNow(farm.startFrom), [farm])

  const symbol: string = getFiatSymbol()

  const totalValueFormatted =
    farm.liquidity && farm.liquidity.gt(0)
      ? `${symbol}${getFiatValue(farm.liquidity).toNumber().toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : ''

  // const lpLabel = farm.lpSymbol && farm.lpSymbol.toUpperCase().replace('VVS', '') // FIXME replace ??
  const lpLabel = farm.lpSymbol && farm.lpSymbol.toUpperCase()

  const liquidityUrlPathParts = getLiquidityUrlPathParts({
    quoteTokenAddress: farm.quoteToken.address,
    tokenAddress: farm.token.address,
  })
  const addLiquidityUrl = `${BASE_ADD_LIQUIDITY_URL}/${liquidityUrlPathParts}`
  const lpAddress = getAddress(farm.lpAddresses)
  const zapUrlPathParts = getZapUrlPathParts({ lpAddress })
  const zapUrl = `${BASE_ZAP_URL}/${zapUrlPathParts}`

  const boostFactorData = useFarmBoostFactor(farm.pid)
  const isBoostFactorDataLoading = !boostFactorData.loaded
  const { boostFactor } = boostFactorData
  const totalFactor = useMemo(() => {
    return farm.isBoosted ? boostFactor : MAX_TOTAL_BOOST_FACTOR
  }, [boostFactor, farm.isBoosted])

  const { isBoosted } = farm
  const ecoFarmConfig = isEcoFarmSupported(farm.pid) ? ecoFarm[farm.ecoFarm] : null
  const xVVSBoosted = isBoosted && xVVSStaked
  const { isEcoBoosted } = useGetEcoBoostFactor(farm.pid)
  const canBoost = farm.enableBoost || farm.ecoFarm

  return (
    <StyledCard
      isActive={false}
      isFinished={farm.isFinished}
      ribbon={farm.isFinished && <CardRibbon variantColor="grey" text={t('Finished')} />}
    >
      <FarmCardInnerContainer>
        {(xVVSBoosted || canBoost) && (isBoostFarm(farm) || farm.isBoosted) && (
          <Flex mb="16px" gap="4px" alignItems="center">
            {xVVSBoosted && <BoostedTag type="xVVS" />}
            {isEcoBoosted && <BoostedTag type="CRONOS NFT" />}
            {!xVVSBoosted && !isEcoBoosted && canBoost && <BoostAvailableTag />}
            {farm.boostFarmExpired && <BoostExpiredTag mt="2px" mb="2px" />}
          </Flex>
        )}
        <CardHeading
          lpLabel={lpLabel}
          token={farm.token}
          quoteToken={farm.quoteToken}
          suffix={farm.suffix}
          multiYieldLabel={farm.multiYieldLabel}
        />
        {!removed && (
          <Flex justifyContent="space-between" alignItems="center">
            {!hasFarmStarted ? (
              <>
                <Text color="textSubtle">{t('Starts in')}</Text>
                <Text style={{ display: 'flex', alignItems: 'center' }}>
                  <Countdown startFrom={farm.startFrom} marginRight="2px" />
                </Text>
              </>
            ) : (
              <>
                <Text color="textSubtle">
                  {t(
                    isBlindMode()
                      ? 'VVS Allocation'
                      : (isBoosted && xVVSStaked && !farm.boostFarmExpired) || isEcoBoosted
                      ? 'Boosted APR'
                      : 'APR',
                  )}
                </Text>
                <Text style={{ display: 'flex', alignItems: 'center' }}>
                  {farm.apr ? (
                    <ApyButton
                      variant="text-and-button"
                      pid={farm.pid}
                      version={farm.version}
                      lpSymbol={farm.lpSymbol}
                      lpLabel={lpLabel}
                      addLiquidityUrl={addLiquidityUrl}
                      vvsPrice={vvsPrice}
                      apr={farm.apr}
                      vvsApr={farm.vvsRewardsApr}
                      lpApr={farm.lpRewardsApr}
                      poolLiquidity={new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)}
                      poolWeight={farm.poolWeight}
                      rewardData={farm.rewardData}
                      farms={farms}
                    />
                  ) : (
                    <Skeleton height={24} width={80} />
                  )}
                </Text>
              </>
            )}
          </Flex>
        )}
        <Flex justifyContent="space-between">
          <Text color="textSubtle">{t('Pool Rewards /Day')}</Text>
          <PoolRewards
            baseReward={{ token: Tokens.vvs, rewardPerDay: getVVSRewardPerDay(farm.poolWeight) }}
            rewardData={farm.rewardData}
          />
        </Flex>
        <CardActionsContainer
          farm={farm}
          lpLabel={lpLabel}
          account={account}
          vvsPrice={vvsPrice}
          displayApr={displayApr}
          addLiquidityUrl={addLiquidityUrl}
          token={farm.token}
          quoteToken={farm.quoteToken}
          version={farm.version}
          farms={farms}
          boostMultiplier={farm.isBoosted && onDashboard ? boostFactor : 0}
        />
      </FarmCardInnerContainer>

      <ExpandingWrapper expanded={showExpandableSection}>
        <ExpandableSectionButton
          onClick={() => setShowExpandableSection(!showExpandableSection)}
          expanded={showExpandableSection}
        />
        {showExpandableSection && (
          <DetailsSection
            removed={removed}
            bscScanAddress={getExplorerLink(lpAddress, 'address', chainId)}
            infoAddress={`/info/farm/${lpAddress}`}
            totalValueFormatted={totalValueFormatted}
            lpLabel={lpLabel}
            lpAddress={lpAddress}
            addLiquidityUrl={addLiquidityUrl}
            zapUrl={zapUrl}
            farm={farm}
          />
        )}
        {!onDashboard &&
          showExpandableSection &&
          farm.enableBoost &&
          !xVVSBoosted &&
          (isBoostFarm(farm) || isBoosted) && <BoostAction farmId={farm.pid} xVVSStaked={xVVSStaked} />}
        {!onDashboard && showExpandableSection && xVVSBoosted && (isBoostFarm(farm) || isBoosted) && (
          <BoostPanel
            isBoosted
            isLoading={isBoostFactorDataLoading}
            boostMultiplier={totalFactor}
            farmId={farm.pid}
            xVVSStaked={xVVSStaked}
          />
        )}
        {!onDashboard && showExpandableSection && ecoFarmConfig && (
          <EcoBoostPanel farmId={farm.pid} ecoFarm={ecoFarmConfig} />
        )}
      </ExpandingWrapper>
    </StyledCard>
  )
}

export default FarmCard
